import React from 'react';
import { inputAnatomy } from '@chakra-ui/anatomy';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  InputGroup,
  Select,
  createMultiStyleConfigHelpers,
  SelectProps as ChakraSelectInputProps,
  forwardRef,
  SelectProps
} from '@chakra-ui/react';

import { CommonFormProps } from '@/components/atomic/v1/types';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(inputAnatomy.keys);
const baseStyle = definePartsStyle({
  field: {
    height: 12,
    border: '2px solid',
    bg: 'form/bg',
    textStyle: 'body.regular'
  }
});

const myomersVariant = definePartsStyle({
  // To implement defined style in the design system for each state of the SelectInput component.
  // The default variant of SelectInput overrides the base style and this new variant is required to ensure that other variant styles are not used.

  field: {
    borderRadius: 'base',
    borderColor: 'form/border',
    height: '48px',
    _disabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
      borderColor: 'form/border',
      bg: 'form/bg:disable'
    },
    _hover: {
      borderColor: 'form/border:hover',
      bg: 'form/bg:hover',
      cursor: 'pointer',
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        borderColor: 'form/border',
        bg: 'form/bg:disable'
      }
    },
    _focus: {
      borderColor: 'form/border:active',
      bg: 'form/bg:active'
    },
    _active: {
      borderColor: 'form/border:active',
      bg: 'form/bg:active'
    },
    _readOnly: {
      borderColor: 'form/border',
      bg: 'form/bg:readonly'
    },
    _invalid: {
      borderColor: 'form/border:error',
      bg: 'form/bg:error'
    }
  }
});

export const selectInputTheme = defineMultiStyleConfig({
  baseStyle,
  variants: {
    myomers: myomersVariant
  },
  defaultProps: {
    variant: 'myomers'
  }
});

export type CommonSelectInputProps = {
  placeholder?: ChakraSelectInputProps['placeholder'];
  defaultValue?: ChakraSelectInputProps['defaultValue'];
};

export type SelectInputProps = SelectProps &
  Pick<CommonFormProps, 'error' | 'label'> &
  CommonSelectInputProps & {
    options: Array<{ label: string; value: string | number }>;
    // onChange?: (value: string | number) => void;
    onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  };

/**
 * Low level myOMERS SelectInput component.
 *
 * Use the `label` prop to add a label for the SelectInput.
 * 
 * Example usage:
 * ```
 * <SelectInput
        label="Select label"
        placeholder="Hint Text"
        options={[
            { label: 'Option 1', value: 'option1' },
            { label: 'Option 2', value: 'option2' },
            { label: 'Option 3', value: 'option3' }
        ]}
        onChange={(value) => console.log(value)}
    />
 * ```
 */

const SelectInput = forwardRef<SelectInputProps, 'select'>(
  (
    {
      id,
      label,
      error,
      isReadOnly = false,
      isRequired = false,
      isDisabled = false,
      placeholder = '',
      defaultValue,
      options,
      ...rest
    },
    ref
  ) => {
    return (
      <FormControl isReadOnly={isReadOnly} isRequired={isRequired} isInvalid={!!error} isDisabled={isDisabled} id={id}>
        {label && <FormLabel>{label}</FormLabel>}
        <InputGroup>
          <Select
            ref={ref}
            borderColor="form/border"
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            isInvalid={!!error}
            pointerEvents={isReadOnly ? 'none' : 'inherit'}
            placeholder={placeholder}
            defaultValue={defaultValue}
            {...rest}
            data-testid={`select-input-${rest.name}`}
          >
            {options.map(({ value, label: optionLabel, ...optionProps }) => (
              <option key={value} value={value} {...optionProps}>
                {optionLabel}
              </option>
            ))}
          </Select>
        </InputGroup>
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>
    );
  }
);

export default SelectInput;
